<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ props }" />
    </template>

    <v-form ref="form" data-cy="podMenu-tools-serviceMediaConverter-form">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ $t('routes.pod.serviceMediaConverter.convertToSocialMedia') }}</span>
          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="scrollable mt-4 pt-4">
          <div data-cy="podMenu-tools-serviceMediaConverter-selectPlatform">
            <v-select
              v-model="selectedPlatform"
              v-bind:items="videoPlatform"
              v-bind:label="$t('routes.pod.serviceMediaConverter.selectPlatform')"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled"
              flat required single-line
            />
          </div>

          <div data-cy="podMenu-tools-serviceMediaConverter-selectResolution">
            <v-select
              v-model="selectedResolution"
              v-bind:items="videoResolution"
              v-bind:label="$t('routes.pod.serviceMediaConverter.selectResolution')"
              item-title="name"
              item-value="value"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled"
              flat required single-line return-object
            />
          </div>

          <div class="d-flex flex-column font-italic" v-if="selectedResolution">
            <div class="flex-grow-1" data-cy="podMenu-tools-serviceMediaConverter-aspectRatio">
              {{ $t('routes.pod.serviceMediaConverter.aspectRatio') }}:
              {{ selectedResolution.aspectRatio }}
            </div>
            <div class="flex-grow-1"
                 data-cy="podMenu-tools-serviceMediaConverter-recommendedFrames">
              {{ $t('routes.pod.serviceMediaConverter.recommendedFrames') }}:
              {{ selectedResolution.frames }}
            </div>
            <div class="flex-grow-1"
                 data-cy="podMenu-tools-serviceMediaConverter-recommendedLength">
              {{ $t('routes.pod.serviceMediaConverter.recommendedLength') }}:
              {{ selectedResolution.duration }} {{ $t('routes.pod.serviceMediaConverter.seconds') }}
            </div>
          </div>

          <v-alert
            v-if="durationExceeded"
            class="mt-2"
            color="#3dabff"
            density="compact"
            variant="outlined"
            data-cy="podMenu-tools-serviceMediaConverter-alertDurationExceeded"
          >
            {{ $t('routes.pod.serviceMediaConverter.durationExceeded').replace(':platform', selectedPlatform) }}
          </v-alert>

          <div v-show="successMessage"
               class="bg-success"
               data-cy="podMenu-tools-serviceMediaConverter-successMessage"
          >
            {{ successMessage }}
          </div>
          <div v-show="errorMessage"
               class="bg-error"
               data-cy="podMenu-tools-serviceMediaConverter-errorMessage"
          >
            {{ errorMessage }}
          </div>

          <UtilityPodsErrors data-cy="podMenu-tools-serviceMediaConverter-utilityPodsError" />

        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            v-on:click.prevent="transcodeFile"
            v-bind:disabled="isSubmitDisabled"
            color="primary"
            variant="flat"
            data-cy="podMenu-tools-serviceMediaConverter-save-btn"
          >
            {{ $t('routes.pod.serviceMediaConverter.convert') }}
          </v-btn>

          <v-btn variant="text" @click="dialog = false">
            {{ preventDoubleClick ? $t('common.close') : $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import { useServiceStore } from '@/stores/ServiceStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "ServiceMediaConverterDialog",
  props: {
    pod: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedPlatform: null,
      selectedResolution: null,

      successMessage: "",
      errorMessage: "",

      preventDoubleClick: false,
      durationExceeded: false,

      serviceActivated: false,
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.updatePodsUtility();
      } else {
        this.resetForm();
      }
    },
    selectedPlatform: function () {
      this.selectedResolution = null;
      if (this.durationExceeded) {
        this.durationExceeded = false;
      }
    },
    selectedResolution: function (resolution) {
      if (resolution && (this.rawAsset.duration > resolution.duration)) {
        this.durationExceeded = true;
      }
    },
  },
  methods: {
    updatePodsUtility: function() {
      let utilityPayload = {
        serviceAlias: this.getMediaTranscoderService.alias,
        pods: [this.pod],
      }

      const serviceStore = useServiceStore();
      return serviceStore.updatePodsUtility(utilityPayload);
    },
    transcodeFile: function() {
      if (!this.selectedPlatform) {
        this.errorMessage = this.$t('routes.pod.serviceMediaConverter.error.notPlatformSelected');
        return;
      }

      if (!this.selectedResolution) {
        this.errorMessage = this.$t('routes.pod.serviceMediaConverter.error.notResolutionSelected');
        return;
      }

      this.resetForm();

      this.preventDoubleClick = true;
      setTimeout(() => {
        self.preventDoubleClick = false;
      }, 10000);

      let self = this;

      this.updatePodsUtility()
        .then(function() {
          const serviceStore = useServiceStore();
          const runnable = serviceStore.getRunnablePods;

          if (runnable.length === 0) {
            throw { response: { data: { error: { message: self.$t("routes.pod.serviceMediaConverter.operationError")}}}}
          }

          let payload = {
            serviceAlias: self.getMediaTranscoderService.alias,
            utilityPods: runnable,
            params: {
              platform: self.selectedPlatform,
              resolution: self.selectedResolution.value,
            }
          }

          return serviceStore.runService(payload)
        })
        .then(function() {
          if (self.utilityPodsErrors.length === 0) {
            self.successMessage = self.$t("routes.pod.serviceMediaConverter.operationQueued");
          } else {
            const errors = [];
            for (const error of self.utilityPodsErrors) {
              errors.push(self.$localizeMessage(error.message));
            }

            self.errorMessage = errors.join('<br/>');
          }
        })
        .catch(function(error) {
          console.log('error', error);
          if (error && error.message) {
            self.errorMessage = self.$localizeMessage(error.message);
          } else {
            self.errorMessage = self.$t('backend.system-error');
          }

          self.preventDoubleClick = false;
        });
    },
    resetForm: function() {
      this.errorMessage = '';
      this.successMessage = '';
      this.serviceActivated = false;
    }
  },
  computed: {
    rawAsset: function() {
      if (this.pod.asset && this.pod.asset.raw && this.pod.asset.raw.length > 0) {
        return this.pod.asset.raw[0];
      }

      return null;
    },
    getMediaTranscoderService: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getMediaTranscoder;
    },
    utilityPodsErrors: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPodsErrors;
    },
    isSubmitDisabled: function() {
      if (this.utilityPodsErrors.length > 0 || this.preventDoubleClick) {
        return true;
      }

      return false;
    },
    videoPlatform: function() {
      return [
        'Facebook', 'Instagram', 'LinkedIn', 'Snapchat',
        'TikTok', 'Twitter', 'YouTube',
      ];
    },
    videoResolution: function() {
      const resolutions = {
        twitter: [
          {
            name: "1280x720 (9:16)",
            value: "1280x720_9x16",
            aspectRatio: "9:16",
            frames: "30-60",
            duration: 140,
          }
        ],
        snapchat: [
          {
            name: "1920x1080 (9:16)",
            value: "1920x1080_9x16",
            aspectRatio: "9:16",
            frames: "30-60",
            duration: 10,
          }
        ],
        youtube: [
          {
            name: "1920x1080 (9:16)",
            value: "1920x1080_9x16",
            aspectRatio: "9:16",
            frames: "30-60",
            duration: 43200,
          }
        ],
        linkedin: [
          {
            name: "1920x1080 (9:16)",
            value: "1920x1080_9x16",
            aspectRatio: "9:16",
            frames: "30-60",
            duration: 1800,
          },
          {
            name: "1920x800 (1:2.4)",
            value: "1920x800_1x2.4",
            aspectRatio: "1:2.4",
            frames: "30-60",
            duration: 600,
          }
        ],
        tiktok: [
          {
            name: "1920x1080 (9:16)",
            value: "1920x1080_9x16",
            aspectRatio: "9:16",
            frames: "30-60",
            duration: 60,
          },
        ],
        facebook: [
          {
            name: "1920x1080 (9:16)",
            value: "1920x1080_9x16",
            aspectRatio: "9:16",
            frames: 30,
            duration: 14400,
          },
          {
            name: "1280x720 (9:16)",
            value: "1280x720_9x16",
            aspectRatio: "9:16",
            frames: 30,
            duration: 2700,
          }
        ],
        instagram: [
          {
            name: "1920x1080 (9:16)",
            value: "1920x1080_9x16",
            aspectRatio: "9:16",
            frames: "30-60",
            duration: 60,
          },
        ],
      };

      if (this.selectedPlatform) {
        const selectedPlatform = this.selectedPlatform.toLowerCase();
        if (selectedPlatform in resolutions) {
          return resolutions[selectedPlatform];
        }
      }

      return [];
    },
  }
}
</script>

<style>
</style>
